import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Composant Breadcrumbs avec support SEO et Schema.org
 * @param {Array} items - Liste des éléments du breadcrumb
 * @param {string} className - Classes CSS additionnelles
 * @param {string} separator - Séparateur personnalisé (défaut: ChevronRight)
 */
const Breadcrumbs = ({ items = [], className, separator }) => {
  if (!items.length) return null;

  // Générer les données structurées Schema.org
  const generateStructuredData = () => {
    const breadcrumbList = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": items.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.label,
        "item": item.href ? `${window.location.origin}${item.href}` : undefined
      }))
    };

    return JSON.stringify(breadcrumbList);
  };

  const Separator = separator || (() => <ChevronRight className="h-4 w-4 text-muted-foreground" />);

  return (
    <>
      {/* Données structurées Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: generateStructuredData() }}
      />
      
      {/* Navigation breadcrumb */}
      <nav 
        aria-label="Fil d'Ariane" 
        className={cn("flex items-center space-x-1 text-sm text-muted-foreground mb-4", className)}
        role="navigation"
      >
        <ol className="flex items-center space-x-1">
          {items.map((item, index) => {
            const isLast = index === items.length - 1;
            
            return (
              <li key={index} className="flex items-center">
                {index > 0 && (
                  <span className="mx-2 flex-shrink-0">
                    <Separator />
                  </span>
                )}
                
                {item.href && !isLast ? (
                  <Link
                    to={item.href}
                    className="hover:text-foreground transition-colors duration-200 flex items-center gap-1"
                    title={item.title || `Aller à ${item.label}`}
                  >
                    {item.icon && <item.icon className="h-4 w-4" />}
                    <span>{item.label}</span>
                  </Link>
                ) : (
                  <span 
                    className={cn(
                      "flex items-center gap-1",
                      isLast ? "text-foreground font-medium" : "text-muted-foreground"
                    )}
                    aria-current={isLast ? "page" : undefined}
                  >
                    {item.icon && <item.icon className="h-4 w-4" />}
                    <span>{item.label}</span>
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
};

/**
 * Hook pour générer automatiquement des breadcrumbs basés sur l'URL
 * @param {Object} customItems - Éléments personnalisés à ajouter/remplacer
 * @returns {Array} - Liste des éléments breadcrumb
 */
export const useBreadcrumbs = (customItems = {}) => {
  const path = window.location.pathname;
  const segments = path.split('/').filter(Boolean);
  
  const items = [
    { label: 'Accueil', href: '/', icon: Home }
  ];

  let currentPath = '';
  
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    
    // Vérifier s'il y a un élément personnalisé pour ce segment
    if (customItems[currentPath]) {
      items.push(customItems[currentPath]);
    } else {
      // Génération automatique basée sur le segment
      let label = segment;
      let href = currentPath;
      
      // Transformations courantes
      if (segment === 'cours') {
        label = 'Cours';
      } else if (segment === 'courses') {
        label = 'Tous les cours';
      } else if (segment === 'pass-codeur') {
        label = 'Pass Codeur';
      } else if (segment === 'project') {
        label = 'Projets';
      } else if (segment === 'play') {
        label = 'Jeux partagés';
      } else if (segment === 'profile') {
        label = 'Profil';
      } else {
        // Décoder l'URL et capitaliser
        label = decodeURIComponent(segment)
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
      
      // Si c'est le dernier segment, ne pas mettre de href
      if (index === segments.length - 1) {
        href = null;
      }
      
      items.push({ label, href });
    }
  });

  return items;
};

/**
 * Générateur de breadcrumbs spécialisé pour les cours
 * @param {Object} courseInfo - Informations du cours
 * @param {string} chapterTitle - Titre du chapitre actuel
 * @returns {Array} - Breadcrumbs pour les cours
 */
export const generateCourseBreadcrumbs = (courseInfo, chapterTitle = null) => {
  const items = [
    { label: 'Accueil', href: '/', icon: Home },
    { label: 'Cours', href: '/courses' }
  ];

  if (courseInfo) {
    const courseLabel = courseInfo.isFree 
      ? `${courseInfo.title}`
      : courseInfo.title;
    
    items.push({
      label: courseLabel,
      href: chapterTitle ? `/cours/${courseInfo.id}` : null
    });

    if (chapterTitle) {
      items.push({
        label: chapterTitle,
        href: null // Page actuelle
      });
    }
  }

  return items;
};

/**
 * Générateur de breadcrumbs pour les projets
 * @param {string} username - Nom d'utilisateur
 * @param {string} projectName - Nom du projet
 * @param {boolean} isPlaying - Si on est sur la page de jeu
 * @returns {Array} - Breadcrumbs pour les projets
 */
export const generateProjectBreadcrumbs = (username = null, projectName = null, isPlaying = false) => {
  const items = [
    { label: 'Accueil', href: '/', icon: Home }
  ];

  if (isPlaying) {
    items.push({ label: 'Jeux partagés', href: '/play' });
    
    if (username) {
      items.push({
        label: `Projets de ${username}`,
        href: projectName ? `/play/${username}` : null
      });
      
      if (projectName) {
        items.push({
          label: projectName,
          href: null // Page actuelle
        });
      }
    }
  } else {
    items.push({ label: 'Mes projets', href: '/project' });
    
    if (projectName) {
      items.push({
        label: projectName,
        href: null // Page actuelle
      });
    }
  }

  return items;
};

export default Breadcrumbs;
